import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { OnBoarding } from '@questlabs/react-sdk';
import { useAuth } from '../context/AuthContext';
import questConfig from '../config/questConfig';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiTarget, FiUsers, FiTrendingUp } = FiIcons;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [answers, setAnswers] = useState({});

  const userId = user?.userId || localStorage.getItem('userId');
  const token = user?.token || localStorage.getItem('token');

  const getAnswers = () => {
    // Navigate to main app after onboarding completion
    navigate('/');
  };

  const features = [
    {
      icon: FiTarget,
      title: "Personalized Experience",
      description: "We'll customize your experience based on your preferences"
    },
    {
      icon: FiUsers,
      title: "Connect with Pros",
      description: "Find the perfect professionals for your specific needs"
    },
    {
      icon: FiTrendingUp,
      title: "Track Your Progress",
      description: "Monitor your service requests and build relationships"
    }
  ];

  if (!userId || !token) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="min-h-screen flex">
        {/* Left Section - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
          <div className="relative z-10 flex flex-col justify-center px-12">
            <div className="mb-8">
              <Link to="/" className="flex items-center space-x-3 mb-6 group">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm group-hover:bg-opacity-30 transition-all">
                  <SafeIcon icon={FiStar} className="text-white text-2xl" />
                </div>
                <span className="text-3xl font-bold hover:text-blue-100 transition-colors">ServiceList</span>
              </Link>
              <h1 className="text-4xl font-bold mb-4">
                Let's Get Started!
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                We're setting up your personalized experience. This will only take a few minutes.
              </p>
            </div>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <SafeIcon icon={feature.icon} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{feature.title}</h3>
                    <p className="text-blue-100">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-10 right-10 w-32 h-32 bg-white bg-opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-white bg-opacity-10 rounded-full blur-2xl"></div>
        </div>

        {/* Right Section - Onboarding Component */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/" className="flex items-center justify-center space-x-2 mb-4 group">
                <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center group-hover:bg-primary-700 transition-colors">
                  <SafeIcon icon={FiStar} className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">ServiceList</span>
              </Link>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h2>
              <p className="text-gray-600">Let's personalize your experience</p>
            </div>

            {/* Quest Onboarding Component */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="hidden lg:block text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Setup Your Profile</h2>
                <p className="text-gray-600">Help us understand your needs better</p>
              </div>
              
              <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                <OnBoarding
                  userId={userId}
                  token={token}
                  questId={questConfig.QUEST_ONBOARDING_QUESTID}
                  answer={answers}
                  setAnswer={setAnswers}
                  getAnswers={getAnswers}
                  accent={questConfig.PRIMARY_COLOR}
                  singleChoose="modal1"
                  multiChoice="modal2"
                >
                  <OnBoarding.Header />
                  <OnBoarding.Content />
                  <OnBoarding.Footer />
                </OnBoarding>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;